import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Coins } from "lucide-react";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startCreatePurchaseCreditsPaymentLink } from "../../actions/payments";
import Button from "../Buttons/Button";
import {
  MODAL_PURCHASE_CREDITS_CREDITS_LABEL,
  MODAL_PURCHASE_CREDITS_PRICE_PER_CREDIT,
  MODAL_PURCHASE_CREDITS_PURCHASE_BUTTON,
  PAYWALL_INSUFFICIENT_CREDITS_BUY_DESCRIPTION,
  PAYWALL_INSUFFICIENT_CREDITS_BUY_TITLE,
} from "../../i18n/keysTranslations";
import {
  BOX_SHADOW_THEME,
  GRAY_100,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";
import { DEFAULT_CREDIT_PURCHASE_PACKAGES } from "../../utils/creditActionCosts";

const PaywallCreditPackages = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const creditPackages =
    useSelectorApp((state) => state.payments?.creditPurchasePackages) ??
    DEFAULT_CREDIT_PURCHASE_PACKAGES;
  const [selectedCredits, setSelectedCredits] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedCredits == null) return;
    const stillExists = creditPackages.some(
      (pkg) => pkg.credits === selectedCredits
    );
    if (!stillExists) setSelectedCredits(null);
  }, [creditPackages, selectedCredits]);

  const onSelectPackage = (credits) => {
    setSelectedCredits(credits);
  };

  const onPackageKeyDown = ({ event, credits }) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelectPackage(credits);
  };

  const onPurchaseCredits = async () => {
    if (!selectedCredits) return;
    setIsLoading(true);
    try {
      const response = await dispatch(
        startCreatePurchaseCreditsPaymentLink({
          quantity: selectedCredits,
          purchaseKind: "credits",
        })
      );
      if (response?.ok && response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch {
      // The thunk already reports LaunchError.
    } finally {
      setIsLoading(false);
    }
  };

  const rowSx = ({ theme, isSelected }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: BORDER_RADIUS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: `1px solid ${INPUT_COLOR_BORDER}`,
    borderColor: isSelected ? theme.palette.primary.main : INPUT_COLOR_BORDER,
    backgroundColor: isSelected
      ? theme.palette.action.selected
      : theme.palette.background.paper,
    boxShadow: BOX_SHADOW_THEME,
    "&:hover": {
      backgroundColor: isSelected
        ? theme.palette.action.selected
        : theme.palette.action.hover,
    },
  });

  const iconWrapSx = ({ theme, isSelected }) => ({
    width: 40,
    height: 40,
    borderRadius: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isSelected ? theme.palette.text.primary : GRAY_100,
    color: isSelected
      ? theme.palette.background.paper
      : theme.palette.text.primary,
    flexShrink: 0,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        flex: 1,
      }}
    >
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}
        >
          {t(PAYWALL_INSUFFICIENT_CREDITS_BUY_TITLE)}
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            color: "text.secondary",
            fontSize: "0.9rem",
            lineHeight: 1.45,
          }}
        >
          {t(PAYWALL_INSUFFICIENT_CREDITS_BUY_DESCRIPTION)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {creditPackages.map((pkg) => {
          const isSelected = selectedCredits === pkg.credits;

          return (
            <Box
              key={pkg.credits}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => onSelectPackage(pkg.credits)}
              onKeyDown={(event) =>
                onPackageKeyDown({ event, credits: pkg.credits })
              }
              sx={(theme) => rowSx({ theme, isSelected })}
            >
              <Box sx={(theme) => iconWrapSx({ theme, isSelected })}>
                <Coins size={20} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "text.primary",
                  }}
                >
                  {t(MODAL_PURCHASE_CREDITS_CREDITS_LABEL, {
                    credits: pkg.credits.toLocaleString(),
                  })}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  }}
                >
                  {t(MODAL_PURCHASE_CREDITS_PRICE_PER_CREDIT, {
                    price: pkg.pricePerCredit.toFixed(2),
                  })}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "text.primary",
                  flexShrink: 0,
                }}
              >
                ${pkg.price.toLocaleString()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={!selectedCredits}
        loading={isLoading}
        onClick={onPurchaseCredits}
        sx={{ mt: "auto", minWidth: 0 }}
      >
        {t(MODAL_PURCHASE_CREDITS_PURCHASE_BUTTON)}
      </Button>
    </Box>
  );
};

export default PaywallCreditPackages;
